import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

/* ============================================================================
   Health Check + Security Monitoring — READ-ONLY (2026-10-17)
   Visible only to the platform super admin and Taha (server-enforced allowlist).
   Calls GET /api/health-check and GET /api/health-check/security; nothing on
   this page can change any configuration.
   ============================================================================ */

type Check = { group: string; id: string; label: string; status: 'ok' | 'warn' | 'crit' | 'info'; value: string; detail: string; issue?: string; action?: string };
type Report = {
  kind: string; generated_at: string;
  overall: { status: 'ok' | 'warn' | 'crit' | 'info'; counts: Record<string, number> };
  checks: Check[]; details?: any;
};

const HEALTH_GROUPS: [string, string][] = [
  ['trunks', 'Carrier Trunks'], ['gateways', 'Gateways'], ['voice', 'CallEssential Voice Engine'],
  ['services', 'CE Services'], ['traffic', 'Calls & Billing Records'], ['database', 'CE Database'],
  ['system', 'Server Resources'], ['patches', 'Patch Updates'], ['backup', 'Backups'], ['tls', 'Security Certificates'],
];
const SECURITY_GROUPS: [string, string][] = [
  ['findings', 'Known Open Issues'], ['breach', 'Breach Indicators'], ['intrusion', 'Intrusion Protection'],
  ['logins', 'Sign-in Activity'], ['accounts', 'Administrator Accounts'], ['fraud', 'Call Misuse Indicators'],
  ['posture', 'Vulnerability Posture'],
];
const ST: Record<string, { label: string; cls: string; icon: string }> = {
  ok:   { label: 'Healthy',   cls: 'ok',   icon: '✓' },
  warn: { label: 'Attention', cls: 'warn', icon: '!' },
  crit: { label: 'Critical',  cls: 'crit', icon: '✕' },
  info: { label: 'Info',      cls: 'info', icon: 'i' },
};

const CSS = `
.hc{max-width:1180px}
.hc-tabs{display:flex;gap:4px;border-bottom:1px solid #e3e6eb;margin-bottom:16px;flex-wrap:wrap}
.hc-tab{background:none;border:0;padding:10px 16px;font-size:14px;font-weight:600;color:#5b6474;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-1px}
.hc-tab.on{color:var(--z-blue,#2D8CFF);border-bottom-color:var(--z-blue,#2D8CFF)}
.hc-banner{display:flex;align-items:center;gap:16px;padding:16px 18px;border-radius:12px;margin-bottom:16px;flex-wrap:wrap}
.hc-banner.ok{background:#e9f8ef;border:1px solid #b7e6c8}.hc-banner.warn{background:#fff6e5;border:1px solid #f5d79a}.hc-banner.crit{background:#fdecec;border:1px solid #f3b5b5}
.hc-banner .big{font-size:18px;font-weight:700;color:#1f2733}
.hc-banner .sub{font-size:13px;color:#4a5363;margin-top:2px}
.hc-dot{width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:18px;flex:none}
.hc-dot.ok{background:#1f9d55}.hc-dot.warn{background:#e0a100}.hc-dot.crit{background:#d93636}.hc-dot.info{background:#7a8496}
.hc-tools{margin-left:auto;display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.hc-tools label{font-size:13px;color:#4a5363;display:flex;gap:6px;align-items:center}
.hc-btn{border:1px solid #cfd5de;background:#fff;border-radius:8px;padding:7px 14px;font-size:13px;font-weight:600;cursor:pointer;color:#1f2733}
.hc-btn.primary{background:var(--z-blue,#2D8CFF);border-color:var(--z-blue,#2D8CFF);color:#fff}
.hc-btn:disabled{opacity:.6;cursor:default}
.hc-counts{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:16px}
.hc-count{background:#fff;border:1px solid #e3e6eb;border-radius:10px;padding:10px 14px;min-width:110px}
.hc-count b{display:block;font-size:22px;color:#1f2733}.hc-count span{font-size:12px;color:#5b6474}
.hc-sec{background:#fff;border:1px solid #e3e6eb;border-radius:12px;margin-bottom:14px;overflow:hidden}
.hc-sec h3{margin:0;padding:12px 16px;font-size:14px;font-weight:700;color:#1f2733;background:#f7f8fa;border-bottom:1px solid #e3e6eb;display:flex;align-items:center;gap:8px}
.hc-sec h3 .pill{margin-left:auto;font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px}
.pill.ok{background:#e9f8ef;color:#167a42}.pill.warn{background:#fff3d6;color:#8a6200}.pill.crit{background:#fdecec;color:#b42323}.pill.info{background:#eef0f4;color:#4a5363}
.hc-row{display:grid;grid-template-columns:26px minmax(160px,1.3fr) minmax(110px,.8fr) 2fr;gap:10px;padding:10px 16px;border-bottom:1px solid #f0f2f5;align-items:start;font-size:13px}
.hc-row:last-child{border-bottom:0}
.hc-row .ic{width:20px;height:20px;border-radius:50%;color:#fff;font-size:11px;font-weight:800;display:flex;align-items:center;justify-content:center}
.ic.ok{background:#1f9d55}.ic.warn{background:#e0a100}.ic.crit{background:#d93636}.ic.info{background:#9aa3b2}
.hc-row .lb{font-weight:600;color:#1f2733}.hc-row .vl{color:#1f2733}.hc-row .dt{color:#5b6474;word-break:break-word}
.hc-row.crit .vl{color:#b42323;font-weight:700}.hc-row.warn .vl{color:#8a6200;font-weight:700}
@media (max-width:720px){.hc-row{grid-template-columns:26px 1fr}.hc-row .vl,.hc-row .dt{grid-column:2}}
.hc-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:14px;margin-bottom:14px}
.hc-mini{background:#fff;border:1px solid #e3e6eb;border-radius:12px;padding:12px 16px}
.hc-mini h4{margin:0 0 8px;font-size:13px;color:#1f2733}
.hc-mini table{width:100%;border-collapse:collapse;font-size:12.5px}
.hc-mini td{padding:4px 0;border-bottom:1px solid #f0f2f5;color:#3b4453}
.hc-mini td:last-child{text-align:right;font-variant-numeric:tabular-nums}
.hc-empty{color:#8a93a3;font-size:12.5px}
.hc-call{background:#fdecec;border:1px solid #f3b5b5;color:#8f1d1d;border-radius:10px;padding:10px 14px;margin-bottom:16px;font-size:13px}
.hc-note{font-size:12px;color:#7a8496;margin-top:8px}
.hc-fix{grid-column:2 / -1;border-radius:8px;padding:9px 12px;font-size:12.5px;line-height:1.45;display:grid;gap:4px}
.hc-fix.warn{background:#fffaf0;border:1px solid #f5e1b3}.hc-fix.crit{background:#fff5f5;border:1px solid #f3c9c9}
.hc-fix b{color:#1f2733}
@media (max-width:720px){.hc-fix{grid-column:1 / -1}}
`;

function fmtTime(s?: string) {
  if (!s) return '';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function MiniTable({ title, rows, empty }: { title: string; rows: [string, React.ReactNode][]; empty: string }) {
  return (
    <div className="hc-mini">
      <h4>{title}</h4>
      {rows.length === 0 ? <div className="hc-empty">{empty}</div> : (
        <table><tbody>{rows.map((r, i) => <tr key={i}><td>{r[0]}</td><td>{r[1]}</td></tr>)}</tbody></table>
      )}
    </div>
  );
}

export default function HealthCheckPage({ view, onSwitch }: { view: 'health' | 'security'; onSwitch?: (page: string) => void }) {
  const [data, setData] = useState<Report | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [auto, setAuto] = useState(true);
  const [copied, setCopied] = useState(false);
  const url = view === 'security' ? '/api/health-check/security' : '/api/health-check';
  const groups = view === 'security' ? SECURITY_GROUPS : HEALTH_GROUPS;

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const r = await axios.get(url + (force ? '?refresh=1' : ''));
      setData(r.data); setErr(null);
    } catch (e: any) {
      setErr(e?.response?.status === 403 ? 'You do not have access to this page.'
        : (e?.response?.data?.error || 'Could not load the report. Please try again.'));
    } finally { setLoading(false); }
  }, [url]);

  useEffect(() => { setData(null); load(false); }, [load]);
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(() => load(false), view === 'security' ? 120000 : 60000);
    return () => clearInterval(t);
  }, [auto, load, view]);

  const byGroup = useMemo(() => {
    const m: Record<string, Check[]> = {};
    (data?.checks || []).forEach((c) => { (m[c.group] = m[c.group] || []).push(c); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => ({ crit: 0, warn: 1, ok: 2, info: 3 } as any)[a.status] - ({ crit: 0, warn: 1, ok: 2, info: 3 } as any)[b.status]));
    return m;
  }, [data]);

  const groupStatus = (cs: Check[]) => cs.some((c) => c.status === 'crit') ? 'crit' : cs.some((c) => c.status === 'warn') ? 'warn' : 'ok';

  const copyReport = () => {
    if (!data) return;
    const title = view === 'security' ? 'CallEssential Security Monitoring Report' : 'CallEssential Health Check Report';
    const lines = [title, `Generated: ${fmtTime(data.generated_at)}`, `Overall: ${ST[data.overall.status].label}`, ''];
    groups.forEach(([g, name]) => {
      const cs = byGroup[g]; if (!cs?.length) return;
      lines.push(`== ${name} ==`);
      cs.forEach((c) => {
        lines.push(`[${ST[c.status].label.toUpperCase()}] ${c.label}: ${c.value}${c.detail ? ' — ' + c.detail : ''}`);
        if (c.issue) lines.push(`    Issue: ${c.issue}`);
        if (c.action) lines.push(`    Corrective action: ${c.action}`);
      });
      lines.push('');
    });
    const txt = lines.join('\n');
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(txt).then(done).catch(() => {});
    else { const ta = document.createElement('textarea'); ta.value = txt; document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); done(); } catch {} document.body.removeChild(ta); }
  };

  const o = data?.overall;
  const d = data?.details || {};
  const headline = !o ? 'Running checks…'
    : o.status === 'crit' ? (view === 'security' ? 'Security issues need attention now' : 'Critical problems detected')
    : o.status === 'warn' ? 'Some items need attention'
    : (view === 'security' ? 'No security problems detected' : 'All systems healthy');

  return (
    <div className="hc">
      <style>{CSS}</style>
      <div className="hc-tabs">
        <button className={`hc-tab ${view === 'health' ? 'on' : ''}`} onClick={() => onSwitch && onSwitch('Health Check')}>System Health</button>
        <button className={`hc-tab ${view === 'security' ? 'on' : ''}`} onClick={() => onSwitch && onSwitch('Security Monitoring')}>Security Monitoring</button>
      </div>

      {err && <div className="hc-call">{err}</div>}

      <div className={`hc-banner ${o ? o.status : 'ok'}`}>
        <div className={`hc-dot ${o ? o.status : 'info'}`}>{o ? ST[o.status].icon : '…'}</div>
        <div>
          <div className="big">{headline}</div>
          <div className="sub">{data ? `Last checked ${fmtTime(data.generated_at)} · read-only monitoring, no settings are changed` : 'Collecting status…'}</div>
        </div>
        <div className="hc-tools">
          <label><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> Auto-refresh</label>
          <button className="hc-btn" onClick={copyReport} disabled={!data}>{copied ? 'Copied ✓' : 'Copy report'}</button>
          <button className="hc-btn primary" onClick={() => load(true)} disabled={loading}>{loading ? 'Checking…' : (view === 'security' ? 'Run security scan' : 'Run health check')}</button>
        </div>
      </div>

      {o && o.status !== 'ok' && o.status !== 'info' && (
        <div className="hc-call">
          <b>What to do:</b> items marked <b>Critical</b> or <b>Attention</b> should be reviewed. If you are not sure, copy the report and send it to the CallEssential Super Admin.
        </div>
      )}

      {o && (
        <div className="hc-counts">
          <div className="hc-count"><b style={{ color: '#d93636' }}>{o.counts.crit || 0}</b><span>Critical</span></div>
          <div className="hc-count"><b style={{ color: '#e0a100' }}>{o.counts.warn || 0}</b><span>Attention</span></div>
          <div className="hc-count"><b style={{ color: '#1f9d55' }}>{o.counts.ok || 0}</b><span>Healthy</span></div>
          <div className="hc-count"><b style={{ color: '#7a8496' }}>{o.counts.info || 0}</b><span>Information</span></div>
        </div>
      )}

      {view === 'security' && data && (
        <div className="hc-grid">
          <MiniTable title="Attack types (24 h)" empty="No attacks recorded"
            rows={(d.attack_types || []).map((r: any) => [r[0], r[1]])} />
          <MiniTable title="Attacks by country (24 h)" empty="No attacks recorded"
            rows={(d.attack_countries || []).map((r: any) => [r[0], r[1]])} />
          <MiniTable title="Top attacking addresses (24 h)" empty="No attacks recorded"
            rows={(d.attack_ips || []).map((r: any) => [r[0], r[1]])} />
          <MiniTable title="Protection rules — blocked now / total" empty="No protection rules reported"
            rows={(d.shield_rules || []).map((r: any) => [r.name, `${r.banned_now} / ${r.banned_total}`])} />
          <MiniTable title="Remote management logins (24 h)" empty="No remote management logins"
            rows={(d.ssh_logins || []).map((r: any) => [`${r.user} ← ${r.ip}`, `${r.time} (${r.method})`])} />
          <MiniTable title="Recent administrator sign-ins" empty="No administrator sign-ins in 7 days"
            rows={(d.admin_signins || []).slice(0, 10).map((r: any) => [`${r.user}${r.new_ip ? ' · NEW address' : ''}`, `${r.ip || '?'} · ${fmtTime(r.time)}`])} />
          <MiniTable title="Failed phone/app sign-ins by address (24 h)" empty="None"
            rows={(d.voice_fail_ips || []).map((r: any) => [r[0], r[1]])} />
          <MiniTable title="Top international callers (24 h)" empty="No international calls"
            rows={(d.intl_top_extensions || []).map((r: any) => [`Extension ${r[0]}`, r[1]])} />
        </div>
      )}

      {view === 'health' && data && (d.patch_categories || []).length > 0 && (
        <div className="hc-grid">
          <MiniTable title="Pending updates by component group — updates / security" empty="Fully up to date"
            rows={(d.patch_categories || []).map((r: any) => [r.category, `${r.updates} / ${r.security}`])} />
        </div>
      )}

      {groups.map(([g, name]) => {
        const cs = byGroup[g];
        if (!cs?.length) return null;
        const gs = groupStatus(cs);
        return (
          <div className="hc-sec" key={g}>
            <h3>{name}<span className={`pill ${gs}`}>{ST[gs].label}</span></h3>
            {cs.map((c) => (
              <div className={`hc-row ${c.status}`} key={c.id}>
                <div className={`ic ${c.status}`}>{ST[c.status].icon}</div>
                <div className="lb">{c.label}</div>
                <div className="vl">{c.value}</div>
                <div className="dt">{c.detail}</div>
                {(c.issue || c.action) && (
                  <div className={`hc-fix ${c.status}`}>
                    {c.issue && <div><b>Issue:</b> {c.issue}</div>}
                    {c.action && <div><b>Corrective action:</b> {c.action}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
      {data && <div className="hc-note">This page only reads status information. It cannot change, restart, block or unblock anything.</div>}
    </div>
  );
}
